import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleAddFieldTableRow,
  handleDeleteFieldTableRow,
  handleEditFieldTableRow,
  handleFieldTableChange,
  handleFormChange,
  handleSaveFieldTableRow,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  Checkbox,
  FieldTable,
  Input,
  RadioButton,
  Select,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import getAge from "../services/ageService";
import { getAddresses, getClientPayors, getContact } from "../services/soar";
import { getSchema } from "./joi/validation";

function SCTFCDiagnostic() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [loadStatus, setLoadStatus] = useState({});

  useEffect(() => {
    if (
      submissionid ||
      loadStatus.loaded ||
      loadStatus.loading ||
      !data.SelectedProgram
    ) {
      return;
    }

    const loadRecords = async () => {
      setLoadStatus({ ...loadStatus, loaded: false, loading: true });
      const response = await Promise.all([
        getContact(clientid),
        getClientPayors(clientid),
        getAddresses(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      const contactResponse = response[0];
      const clientPayorsResponse = response[1];
      const addressesResponse = response[2];
      const obj = { ...data };

      const clientInfo = contactResponse[0];

      obj.DOB = {
        "@value": moment(getFieldDate(clientInfo.birthdate)).format(
          moment.HTML5_FMT.DATE
        ),
        "@type": "custom",
      };
      obj.Age = {
        "@value": getAge(getFieldDate(clientInfo.birthdate)),
        "@type": "custom",
      };
      obj.Phone = { "@value": clientInfo.telephone2, "@type": "custom" };

      const clientPayor = clientPayorsResponse[0];

      if (clientPayor !== undefined) {
        obj.Medicaid = { "@value": clientPayor.ccx_id1, "@type": "custom" };
      }

      const address = addressesResponse.filter(
        (address) =>
          address.ccx_expirationdate === undefined ||
          moment(getFieldDate(address.ccx_expirationdate)).isAfter(moment())
      )[0];

      if (address !== undefined) {
        obj.Address = {
          "@value": `${address.ccx_line1}${
            address.ccx_line2 !== undefined
              ? `
${address.ccx_line2}`
              : ""
          }
${address.ccx_city}, ${address.ccx_state} ${address.ccx_postalcode}`,
          "@type": "custom",
        };
      }

      setData(obj);
      setLoadStatus({ ...loadStatus, loaded: true, loading: false });
    };

    loadRecords();
  }, [data, submissionid, loadStatus, clientid]);

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <p>
                This form replaces the DAF and is filled out by all Case
                Coordinators for new clients and any outpatient clients needing
                assessments. This form is updated annually. It is due within 14
                days of placement.
              </p>
              <div className="row">
                <div className="col">
                  <Input
                    name="ClientName"
                    label="Client Name:"
                    value={
                      clientData && clientData.client
                        ? clientData.client.fullName
                        : ""
                    }
                    disabled
                  />
                </div>
                <div className="col">
                  <Input
                    name="Medicaid"
                    label="Medicaid #:"
                    error={errors.Medicaid}
                    value={data.Medicaid["@value"]}
                    onChange={handleChange}
                    identical={compare("Medicaid")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="AssessmentDate"
                    label="Date of Assessment:"
                    error={errors.AssessmentDate}
                    value={data.AssessmentDate["@value"]}
                    onChange={handleChange}
                    identical={compare("AssessmentDate")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
                <div className="col">
                  <Input
                    name="RBHSAdmissionDate"
                    label="Date of Admission into RBHS:"
                    error={errors.RBHSAdmissionDate}
                    value={data.RBHSAdmissionDate["@value"]}
                    onChange={handleChange}
                    identical={compare("RBHSAdmissionDate")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="Age"
                    label="Age:"
                    error={errors.Age}
                    value={data.Age["@value"]}
                    onChange={handleChange}
                    identical={compare("Age")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="DOB"
                    label="Date of birth (DOB):"
                    error={errors.DOB}
                    value={data.DOB["@value"]}
                    onChange={handleChange}
                    identical={compare("DOB")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="Phone"
                    label="Phone Number:"
                    error={errors.Phone}
                    value={data.Phone["@value"]}
                    onChange={handleChange}
                    identical={compare("Phone")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="Address"
                    label="Address:"
                    error={errors.Address}
                    value={data.Address["@value"]}
                    onChange={handleChange}
                    identical={compare("Address")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <br />
              <div className="h6">Beneficiary's demographic information</div>
              <div className="d-flex">
                <div className="me-2">Race of ethnic group:</div>
                <div className="flex-fill">
                  <Input
                    name="Race"
                    error={errors.Race}
                    value={data.Race["@value"]}
                    onChange={handleChange}
                    identical={compare("Race")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="d-flex">
                <div className="me-2">Legal status:</div>
                <div className="flex-fill">
                  <Input
                    name="LegalStatus"
                    error={errors.LegalStatus}
                    value={data.LegalStatus["@value"]}
                    onChange={handleChange}
                    identical={compare("LegalStatus")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="d-flex">
                <div className="me-2">Marital status:</div>
                <div className="flex-fill">
                  <Input
                    name="MaritalStatus"
                    error={errors.MaritalStatus}
                    value={data.MaritalStatus["@value"]}
                    onChange={handleChange}
                    identical={compare("MaritalStatus")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="d-flex">
                <div className="me-2">General physical appearance:</div>
                <div className="flex-fill">
                  <Input
                    name="PhysicalAppearance"
                    error={errors.PhysicalAppearance}
                    value={data.PhysicalAppearance["@value"]}
                    onChange={handleChange}
                    identical={compare("PhysicalAppearance")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="d-flex">
                <div className="me-2">Preferred Language:</div>
                <div className="flex-fill">
                  <Input
                    name="PreferredLanguage"
                    error={errors.PreferredLanguage}
                    value={data.PreferredLanguage["@value"]}
                    onChange={handleChange}
                    identical={compare("PreferredLanguage")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <Input
                name="PresentingProblem"
                label={
                  <>
                    <b>
                      Presenting Problem (Precipitating factors leading to the
                      need for assessment):
                    </b>
                    <br />
                    <i>Include statement in client's own words...</i>
                  </>
                }
                error={errors.PresentingProblem}
                value={data.PresentingProblem["@value"]}
                onChange={handleChange}
                identical={compare("PresentingProblem")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <div className="h6">Mental Status Screen to include:</div>
              <div className="d-flex">
                <div className="me-2">Appearance:</div>
                <div className="flex-fill">
                  <Input
                    name="MSAppearance"
                    error={errors.MSAppearance}
                    value={data.MSAppearance["@value"]}
                    onChange={handleChange}
                    identical={compare("MSAppearance")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="d-flex">
                <div className="me-2">Attitude:</div>
                <div className="flex-fill">
                  <Input
                    name="MSAttitude"
                    error={errors.MSAttitude}
                    value={data.MSAttitude["@value"]}
                    onChange={handleChange}
                    identical={compare("MSAttitude")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="d-flex">
                <div className="me-2">Motor Activity:</div>
                <div className="flex-fill">
                  <Input
                    name="MSMotorActivity"
                    error={errors.MSMotorActivity}
                    value={data.MSMotorActivity["@value"]}
                    onChange={handleChange}
                    identical={compare("MSMotorActivity")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="d-flex">
                <div className="me-2">Affect:</div>
                <div className="flex-fill">
                  <Input
                    name="MSAffect"
                    error={errors.MSAffect}
                    value={data.MSAffect["@value"]}
                    onChange={handleChange}
                    identical={compare("MSAffect")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="d-flex">
                <div className="me-2">Mood:</div>
                <div className="flex-fill">
                  <Input
                    name="MSMood"
                    error={errors.MSMood}
                    value={data.MSMood["@value"]}
                    onChange={handleChange}
                    identical={compare("MSMood")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="d-flex">
                <div className="me-2">Speech:</div>
                <div className="flex-fill">
                  <Input
                    name="MSSpeech"
                    error={errors.MSSpeech}
                    value={data.MSSpeech["@value"]}
                    onChange={handleChange}
                    identical={compare("MSSpeech")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="d-flex">
                <div className="me-2">Thought Content:</div>
                <div className="flex-fill">
                  <Input
                    name="MSThoughtContent"
                    error={errors.MSThoughtContent}
                    value={data.MSThoughtContent["@value"]}
                    onChange={handleChange}
                    identical={compare("MSThoughtContent")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <br />
              <div className="h6">
                Psychiatric Treatment History
                <br />
                <i>
                  Include all MH services received by client in the past.
                  Include age of onset, duration and frequency of episodes;
                  include outpatient, inpatient, and residential treatment with
                  dates. Describe past responses to treatment. Include past
                  medications, symptom relief and client response, if known.
                </i>
              </div>
              <Input
                name="PsychiatricTreatmentHistory"
                error={errors.PsychiatricTreatmentHistory}
                value={data.PsychiatricTreatmentHistory["@value"]}
                onChange={handleChange}
                identical={compare("PsychiatricTreatmentHistory")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <div className="h6">Current Diagnosis - Full</div>
              <FieldTable
                name="CurrentDiagnosis"
                commentsHandler={handleComments}
              >
                <div className="container border-top border-start border-end mb-2">
                  <div className="row border-bottom">
                    <div className="col border-end py-2">Date</div>
                    <div className="col border-end py-2">Diagnosis</div>
                    <div className="col py-2">
                      Clinical Professional
                      <br />
                      (Name and Credentials)
                    </div>
                  </div>
                  {data.CurrentDiagnosis["@value"] &&
                    JSON.parse(data.CurrentDiagnosis["@value"]).map(
                      (item, index) => (
                        <div key={item._id} className="row border-bottom">
                          <div className="col border-end py-2 d-flex">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <>
                                <i
                                  className="fas fa-save me-2 d-print-none"
                                  onClick={() => {
                                    handleSaveRow(index, "CurrentDiagnosis");
                                  }}
                                ></i>
                                <i
                                  className="far fa-trash-alt me-2 d-print-none"
                                  onClick={() => {
                                    handleDeleteRow(index, "CurrentDiagnosis");
                                  }}
                                ></i>
                                <Input
                                  name="Date"
                                  value={item.Date}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "CurrentDiagnosis"
                                    );
                                  }}
                                  disabled={formDisabled}
                                  type="date"
                                />
                              </>
                            ) : (
                              <>
                                {!formDisabled && (
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(index, "CurrentDiagnosis");
                                    }}
                                  ></i>
                                )}
                                {item.Date !== undefined && item.Date !== ""
                                  ? moment(item.Date).format("l")
                                  : null}
                              </>
                            )}
                          </div>
                          <div className="col border-end py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="Diagnosis"
                                value={item.Diagnosis}
                                onChange={(e) => {
                                  handleRowChange(e, index, "CurrentDiagnosis");
                                }}
                                disabled={formDisabled}
                              />
                            ) : (
                              item.Diagnosis
                            )}
                          </div>
                          <div className="col py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="ClinicalProfessional"
                                value={item.ClinicalProfessional}
                                onChange={(e) => {
                                  handleRowChange(e, index, "CurrentDiagnosis");
                                }}
                                disabled={formDisabled}
                              />
                            ) : (
                              item.ClinicalProfessional
                            )}
                          </div>
                        </div>
                      )
                    )}
                </div>
                {!formDisabled ? (
                  <button
                    className="btn btn-primary mb-2"
                    onClick={() => {
                      handleAddRow("CurrentDiagnosis", { IsEditable: true });
                    }}
                  >
                    Add Diagnosis
                  </button>
                ) : null}
                {errors.CurrentDiagnosis && (
                  <div className="alert alert-danger">
                    {errors.CurrentDiagnosis}
                  </div>
                )}
              </FieldTable>
              <br />
              <Input
                name="DiagnosticImpressions"
                label="Diagnostic Impressions:"
                error={errors.DiagnosticImpressions}
                value={data.DiagnosticImpressions["@value"]}
                onChange={handleChange}
                identical={compare("DiagnosticImpressions")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="MedicalHistory"
                label={
                  <>
                    Health / Medical History &amp; Nutritional Status:
                    <br />
                    <i>
                      Include current health/dental status, presence of physical
                      disabilities...
                    </i>
                  </>
                }
                error={errors.MedicalHistory}
                value={data.MedicalHistory["@value"]}
                onChange={handleChange}
                identical={compare("MedicalHistory")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="d-flex">
                <div className="me-2">Primary Physician's Name:</div>
                <Checkbox
                  name="PCPNA"
                  label="Not applicable"
                  checked={data.PCPNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3 flex-fill"
                />
              </div>
              {data.PCPNA["@value"] !== "true" ? (
                <Input
                  name="PCPName"
                  error={errors.PCPName}
                  value={data.PCPName["@value"]}
                  onChange={handleChange}
                  identical={compare("PCPName")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <div className="d-flex">
                <div className="me-2">
                  Current Over-the-Counter Medications:
                </div>
                <Checkbox
                  name="OTCMedicationsNA"
                  label="Not applicable"
                  checked={data.OTCMedicationsNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3 flex-fill"
                />
              </div>
              {data.OTCMedicationsNA["@value"] !== "true" ? (
                <Input
                  name="OTCMedications"
                  error={errors.OTCMedications}
                  value={data.OTCMedications["@value"]}
                  onChange={handleChange}
                  identical={compare("OTCMedications")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <div className="d-flex">
                <div className="me-2">
                  Allergies to Food and Drug Reactions:
                </div>
                <Checkbox
                  name="AllergiesNA"
                  label="Not applicable"
                  checked={data.AllergiesNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3 flex-fill"
                />
              </div>
              {data.AllergiesNA["@value"] !== "true" ? (
                <Input
                  name="Allergies"
                  error={errors.Allergies}
                  value={data.Allergies["@value"]}
                  onChange={handleChange}
                  identical={compare("Allergies")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <div className="d-flex">
                <div className="me-2">Current Medications:</div>
                <Checkbox
                  name="CurrentMedicationsNA"
                  label="No medications reported"
                  checked={data.CurrentMedicationsNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3 flex-fill"
                />
              </div>
              {data.CurrentMedicationsNA["@value"] !== "true" ? (
                <FieldTable
                  name="CurrentMedications"
                  commentsHandler={handleComments}
                >
                  <div className="container border-top border-start border-end mb-2">
                    <div className="row border-bottom">
                      <div className="col border-end py-2">Name</div>
                      <div className="col border-end py-2">Prescriber</div>
                      <div className="col border-end py-2">Dosage</div>
                      <div className="col border-end py-2">Dates</div>
                      <div className="col border-end py-2">Type</div>
                      <div className="col py-2">Reason for Medication</div>
                    </div>
                    {data.CurrentMedications["@value"] &&
                      JSON.parse(data.CurrentMedications["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "CurrentMedications"
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "CurrentMedications"
                                      );
                                    }}
                                  ></i>
                                  <Input
                                    name="Name"
                                    value={item.Name}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "CurrentMedications"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  {!formDisabled && (
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "CurrentMedications"
                                        );
                                      }}
                                    ></i>
                                  )}
                                  {item.Name}
                                </>
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Prescriber"
                                  value={item.Prescriber}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "CurrentMedications"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Prescriber
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Dosage"
                                  value={item.Dosage}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "CurrentMedications"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Dosage
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Dates"
                                  value={item.Dates}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "CurrentMedications"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Dates
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Type"
                                  value={item.Type}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "CurrentMedications"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Type
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Reason"
                                  value={item.Reason}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "CurrentMedications"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Reason
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled ? (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("CurrentMedications", {
                          IsEditable: true,
                        });
                      }}
                    >
                      Add Medication
                    </button>
                  ) : null}
                  {errors.CurrentMedications && (
                    <div className="alert alert-danger">
                      {errors.CurrentMedications}
                    </div>
                  )}
                </FieldTable>
              ) : null}
              <br />
              <Input
                name="FamilyDisorders"
                label={
                  <>
                    Family History of Psychiatric, Neurological or Medical
                    Disorders:
                    <br />
                    <i>
                      Include type of psychiatric, neurological or medical
                      disorder, history of psychiatric hospitalization of family
                      members...
                    </i>
                  </>
                }
                error={errors.FamilyDisorders}
                value={data.FamilyDisorders["@value"]}
                onChange={handleChange}
                identical={compare("FamilyDisorders")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="ViolentHistory"
                label={
                  <>
                    History of Suicidal and/or Violent Behavior and Current
                    Potential for Suicide and/or Violent Behavior:
                    <br />
                    <i>
                      Include early detection of mental illness that is life
                      threatening to self or others. Document situational
                      stressors and other contributing factors. Establish a
                      conclusion regarding potential (i.e., low, moderate, high)
                      and a plan based on the identified potential should be
                      addressed in this DAF as well as the treatment plan...
                    </i>
                  </>
                }
                error={errors.ViolentHistory}
                value={data.ViolentHistory["@value"]}
                onChange={handleChange}
                identical={compare("ViolentHistory")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="IndicationsOfTrauma"
                label={
                  <>
                    Indications of Abuse / Neglect / Exploitation / Family
                    Violence / Screening for History of Trauma:
                    <br />
                    <i>
                      Identify client's role as perpetrator or victim of sexual
                      and/or physical abuse and relationship between perpetrator
                      and victim. Include any history of treatment...
                    </i>
                  </>
                }
                error={errors.IndicationsOfTrauma}
                value={data.IndicationsOfTrauma["@value"]}
                onChange={handleChange}
                identical={compare("IndicationsOfTrauma")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="PsychosocialHistory"
                label={
                  <>
                    Age Appropriate Psychosocial History and Assessment to
                    Include Consideration of Multi-Cultural / Ethnic Influences:
                    <br />
                    <i>
                      Culturally-responsive assessments can include attention to
                      geographic location, language, political status, tribal
                      affiliation, and religious, racial, ethnic and cultural
                      background. Other important factors that contribute to a
                      responsive assessment include attention to age, sexual
                      orientation, and developmental level...
                    </i>
                  </>
                }
                error={errors.PsychosocialHistory}
                value={data.PsychosocialHistory["@value"]}
                onChange={handleChange}
                identical={compare("PsychosocialHistory")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="ChildhoodHistory"
                label={
                  <>
                    Childhood History and Other Age Appropriate Areas of
                    Assessment / Independent Living Skills, Activities of Daily
                    Living, as applicable:
                    <br />
                    <i>
                      For children, examples include growth and development,
                      family effect on child and child effect on family, play
                      and daily activities. Include early detection of mental
                      illness that is life-threatening to self or others.
                      Include the impact of maltreatment on the child...
                    </i>
                  </>
                }
                error={errors.ChildhoodHistory}
                value={data.ChildhoodHistory["@value"]}
                onChange={handleChange}
                identical={compare("ChildhoodHistory")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="FamilyHistory"
                label={
                  <>
                    Family History / Circumstances; Custody Status;
                    Relationships with Family / Significant Others:
                    <br />
                    <i>
                      Describe current living situation, including present
                      members of household and current family dynamics...
                    </i>
                  </>
                }
                error={errors.FamilyHistory}
                value={data.FamilyHistory["@value"]}
                onChange={handleChange}
                identical={compare("FamilyHistory")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="PlacementHistory"
                label={
                  <>
                    Include family and significant others' importance in
                    maintaining support for client:
                    <br />
                    <i>
                      Describe placement history since removed from biological
                      family home, permanency plan, permitted contacts with
                      family and significant others, any barriers to permanency.
                      Describe strengths for any primary family member who
                      remains in the youth support system (i.e., siblings,
                      grandparents as well as parents)...
                    </i>
                  </>
                }
                error={errors.PlacementHistory}
                value={data.PlacementHistory["@value"]}
                onChange={handleChange}
                identical={compare("PlacementHistory")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Environment"
                label={
                  <>
                    Environment and Home; Leisure and Recreation; Financial
                    Status; Usual Social, Peer Group and Environmental Setting:
                    <br />
                    <i>
                      Include involvement in consumer operated or peer services.
                      Include financial assistance and insurance coverage,
                      housing status and current residence, history, and a
                      description of living conditions...
                    </i>
                  </>
                }
                error={errors.Environment}
                value={data.Environment["@value"]}
                onChange={handleChange}
                identical={compare("Environment")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Education"
                label={
                  <>
                    Educational Assessment / History:
                    <br />
                    <i>
                      Include involvement in consumer operated or peer services.
                      Include financial assistance and insurance coverage,
                      housing status and current residence, history, and a
                      description of living conditions...
                    </i>
                  </>
                }
                error={errors.Education}
                value={data.Education["@value"]}
                onChange={handleChange}
                identical={compare("Education")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="d-flex">
                <div className="me-2">Military Service History:</div>
                <RadioButton
                  name="MilitaryService"
                  error={errors.MilitaryService}
                  value={data.MilitaryService["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="flex-fill"
                />
              </div>
              {data.MilitaryService["@value"] !== "No" ? (
                <Input
                  name="MilitaryServiceDetails"
                  label="Describe military history, type of discharge/reason:"
                  error={errors.MilitaryServiceDetails}
                  value={data.MilitaryServiceDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("MilitaryServiceDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <div className="d-flex">
                <div className="me-2">
                  Vocational Assessment / Employment History:
                </div>
                <RadioButton
                  name="EmploymentHistory"
                  error={errors.EmploymentHistory}
                  value={data.EmploymentHistory["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="flex-fill"
                />
              </div>
              {data.EmploymentHistory["@value"] !== "No" ? (
                <Input
                  name="EmploymentHistoryDetails"
                  label="Describe:"
                  error={errors.EmploymentHistoryDetails}
                  value={data.EmploymentHistoryDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("EmploymentHistoryDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <Input
                name="CurrentInvolvement"
                label={
                  <>
                    Current Involvement with Other Community Resources:
                    <br />
                    <i>
                      List client interests, any involvement with community
                      resources, agencies and other support systems...
                    </i>
                  </>
                }
                error={errors.CurrentInvolvement}
                value={data.CurrentInvolvement["@value"]}
                onChange={handleChange}
                identical={compare("CurrentInvolvement")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="d-flex">
                <div className="me-2">
                  Legal Assessment / Sex Offender History:
                </div>
                <RadioButton
                  name="LegalAssessment"
                  error={errors.LegalAssessment}
                  value={data.LegalAssessment["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </div>
              {data.LegalAssessment["@value"] !== "No" ? (
                <Input
                  name="LegalDetails"
                  label="Describe any current or past arrests, convictions, pending charges, child protective or domestic relations litigation, parole/probation status:"
                  error={errors.LegalDetails}
                  value={data.LegalDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("LegalDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <Input
                name="Religion"
                label={
                  <>
                    Religion / Spirituality / Spiritual Orientation:
                    <br />
                    <i>
                      Describe client's personal and family world view, health
                      care belief system, religious and spiritual orientation.
                      To whom does client turn for help in times of illness or
                      distress?
                    </i>
                  </>
                }
                error={errors.Religion}
                value={data.Religion["@value"]}
                onChange={handleChange}
                identical={compare("Religion")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Strengths"
                label="Strengths / Assets:"
                error={errors.Strengths}
                value={data.Strengths["@value"]}
                onChange={handleChange}
                identical={compare("Strengths")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Weaknesses"
                label="Weaknesses / Limitations / Potential Barriers for Client:"
                error={errors.Weaknesses}
                value={data.Weaknesses["@value"]}
                onChange={handleChange}
                identical={compare("Weaknesses")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="ProblemBehaviors"
                label="Maladaptive or Problem Behaviors / Non-Substance Addictive Behaviors:"
                error={errors.ProblemBehaviors}
                value={data.ProblemBehaviors["@value"]}
                onChange={handleChange}
                identical={compare("ProblemBehaviors")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="OverallEvaluation"
                label={
                  <>
                    Evaluation of Language, Self-Care, Visual-Motor and
                    Cognitive Functioning:
                    <br />
                    <i>
                      Results from evaluations by appropriately licensed
                      disciplines, if completed. List date of exam, provider
                      name and credentials and summarize results...
                    </i>
                  </>
                }
                error={errors.OverallEvaluation}
                value={data.OverallEvaluation["@value"]}
                onChange={handleChange}
                identical={compare("OverallEvaluation")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="PsychologicalAssessment"
                label={
                  <>
                    Psychological Assessment:
                    <br />
                    <i>
                      Results of intellectual, projective, neuropsychological
                      and personality testing, if completed. List date of exam,
                      provider name and credentials and summarize results...
                    </i>
                  </>
                }
                error={errors.PsychologicalAssessment}
                value={data.PsychologicalAssessment["@value"]}
                onChange={handleChange}
                identical={compare("PsychologicalAssessment")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="PsychiatricEvaluation"
                label={
                  <>
                    Psychiatric Evaluation:
                    <br />
                    <i>
                      Results from evaluation by psychiatrist, if completed.
                      List date of exam, provider name and credentials and
                      summarize results...
                    </i>
                  </>
                }
                error={errors.PsychiatricEvaluation}
                value={data.PsychiatricEvaluation["@value"]}
                onChange={handleChange}
                identical={compare("PsychiatricEvaluation")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="SexualHistory"
                label={
                  <>
                    Sexual Orientation / History / Issues:
                    <br />
                    <i>Is client sexually active? Gender expression...</i>
                  </>
                }
                error={errors.SexualHistory}
                value={data.SexualHistory["@value"]}
                onChange={handleChange}
                identical={compare("SexualHistory")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              History of Alcohol and Other Drug Use by Client / Family /
              Significant Others / Current Use of Alcohol and Drugs
              <br />
              <RadioButton
                name="SubstanceUseIssue"
                label="Does the client, foster parent or referral source identify this area as an issue?"
                error={errors.SubstanceUseIssue}
                value={data.SubstanceUseIssue["@value"]}
                options={yesNoOptions}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="SubstanceUseReferred"
                label="Has the client ever been referred for drug or alcohol treatment?"
                error={errors.SubstanceUseReferred}
                value={data.SubstanceUseReferred["@value"]}
                options={yesNoOptions}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="SubstanceUseConcern"
                label="Has anyone expressed concern about drug or alcohol abuse for this client?"
                error={errors.SubstanceUseConcern}
                value={data.SubstanceUseConcern["@value"]}
                options={yesNoOptions}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.SubstanceUseIssue["@value"] === undefined ||
              data.SubstanceUseReferred["@value"] === undefined ||
              data.SubstanceUseConcern["@value"] === undefined ||
              data.SubstanceUseIssue["@value"] === "Yes" ||
              data.SubstanceUseReferred["@value"] === "Yes" ||
              data.SubstanceUseConcern["@value"] === "Yes" ? (
                <>
                  <br />
                  Psychoactive Substance Use:
                  <FieldTable
                    name="PsychoactiveHistory"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Substance</div>
                        <div className="col border-end py-2">
                          Frequency of Use
                        </div>
                        <div className="col border-end py-2">Amount</div>
                        <div className="col border-end py-2">Route</div>
                        <div className="col border-end py-2">
                          Date of Last Use
                        </div>
                        <div className="col py-2">Age of First Use</div>
                      </div>
                      {data.PsychoactiveHistory["@value"] &&
                        JSON.parse(data.PsychoactiveHistory["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "PsychoactiveHistory"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "PsychoactiveHistory"
                                        );
                                      }}
                                    ></i>
                                    <Input
                                      name="Substance"
                                      value={item.Substance}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "PsychoactiveHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    {!formDisabled && (
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "PsychoactiveHistory"
                                          );
                                        }}
                                      ></i>
                                    )}
                                    {item.Substance}
                                  </>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Frequency"
                                    value={item.Frequency}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "PsychoactiveHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Frequency
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Amount"
                                    value={item.Amount}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "PsychoactiveHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Amount
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Route"
                                    value={item.Route}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "PsychoactiveHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Route
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="LastUse"
                                    value={item.LastUse}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "PsychoactiveHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.LastUse
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="FirstUse"
                                    value={item.FirstUse}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "PsychoactiveHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.FirstUse
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("PsychoactiveHistory", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Substance
                      </button>
                    ) : null}
                    {errors.PsychoactiveHistory && (
                      <div className="alert alert-danger">
                        {errors.PsychoactiveHistory}
                      </div>
                    )}
                  </FieldTable>
                  <Input
                    name="SubstanceUseTreatmentHistory"
                    label="History of Treatment for Alcohol and Other Drug Abuse:"
                    error={errors.SubstanceUseTreatmentHistory}
                    value={data.SubstanceUseTreatmentHistory["@value"]}
                    onChange={handleChange}
                    identical={compare("SubstanceUseTreatmentHistory")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              ) : null}
              <br />
              <Input
                name="AssessmentSources"
                label={
                  <>
                    Sources of Assessment Information:
                    <br />
                    <i>
                      List names and relationships for those who provided
                      information for this assessment...
                    </i>
                  </>
                }
                error={errors.AssessmentSources}
                value={data.AssessmentSources["@value"]}
                onChange={handleChange}
                identical={compare("AssessmentSources")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <div className="h6">
                Assessment Summary
                <br />
                <i>
                  Include clinical impression of client's current behavioral,
                  cognitive and emotional functioning. Include any indicators
                  for further testing.
                </i>
              </div>
              <Input
                name="BehavioralFunctioning"
                label="Behavioral functioning:"
                error={errors.BehavioralFunctioning}
                value={data.BehavioralFunctioning["@value"]}
                onChange={handleChange}
                identical={compare("BehavioralFunctioning")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="CognitiveFunctioning"
                label="Cognitive functioning:"
                error={errors.CognitiveFunctioning}
                value={data.CognitiveFunctioning["@value"]}
                onChange={handleChange}
                identical={compare("CognitiveFunctioning")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="EmotionalFunctioning"
                label="Emotional functioning:"
                error={errors.EmotionalFunctioning}
                value={data.EmotionalFunctioning["@value"]}
                onChange={handleChange}
                identical={compare("EmotionalFunctioning")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Symptoms"
                label="Primary symptoms and expected focus of treatment:"
                error={errors.Symptoms}
                value={data.Symptoms["@value"]}
                onChange={handleChange}
                identical={compare("Symptoms")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Factors"
                label="Risk factors and protective factors, including steps taken to address identified current risks:"
                error={errors.Factors}
                value={data.Factors["@value"]}
                onChange={handleChange}
                identical={compare("Factors")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h6">Statement of Medical Necessity</div>
              <Input
                name="AssessmentCompletedBy"
                label="Assessment completed by:"
                error={errors.AssessmentCompletedBy}
                value={data.AssessmentCompletedBy["@value"]}
                onChange={handleChange}
                identical={compare("AssessmentCompletedBy")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <p>
                I,{" "}
                {data.AssessmentCompletedBy["@value"] !== undefined ? (
                  <u>{data.AssessmentCompletedBy["@value"]}</u>
                ) : null}
                , certify based on the findings of this Diagnostic Assessment,
                medical necessity is met for the following services to meet the
                needs of the identified beneficiary. In order to support the
                reduction of emotional, behavioral, and psychological distress
                and restoration of the beneficiary to his or her best possibly
                function, I recommend the receipt of rehabilitative services. As
                evidenced by the above diagnosis, noted in the DSMV and ICD10,
                this beneficiary meets the following medical necessity criteria.
              </p>
              <br />
              <div className="h6">Core Services:</div>
              <div className="ms-3">
                Details: Interference with at least two or more of the
                following:
                <div className="ms-3">
                  <Checkbox
                    name="CoreDailyLivingTasks"
                    label="Tasks of Daily Living"
                    checked={data.CoreDailyLivingTasks["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  <Checkbox
                    name="CorePersonalRelationships"
                    label="Personal Relationships"
                    checked={
                      data.CorePersonalRelationships["@value"] === "true"
                    }
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  <Checkbox
                    name="CoreWork"
                    label="Work/School"
                    checked={data.CoreWork["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  <Checkbox
                    name="CoreRecreation"
                    label="Recreation"
                    checked={data.CoreRecreation["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <br />
                Recommendations
                <br />
                <div className="container border">
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">Service</div>
                    <div className="col py-2 border-end"># of Units</div>
                    <div className="col py-2">
                      Frequency (per day/per week/per month)
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="NoServices"
                        label="No Core Services Recommended"
                        checked={data.NoServices["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end"></div>
                    <div className="col py-2"></div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="IndividualTherapy"
                        label="Individual Therapy"
                        checked={data.IndividualTherapy["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="IndividualTherapyUnits"
                        error={errors.IndividualTherapyUnits}
                        value={data.IndividualTherapyUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("IndividualTherapyUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.IndividualTherapy["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="IndividualTherapyFrequency"
                        error={errors.IndividualTherapyFrequency}
                        value={data.IndividualTherapyFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("IndividualTherapyFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.IndividualTherapy["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="FamilyTherapy"
                        label="Family Therapy with Beneficiary"
                        checked={data.FamilyTherapy["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="FamilyTherapyUnits"
                        error={errors.FamilyTherapyUnits}
                        value={data.FamilyTherapyUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("FamilyTherapyUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.FamilyTherapy["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="FamilyTherapyFrequency"
                        error={errors.FamilyTherapyFrequency}
                        value={data.FamilyTherapyFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("FamilyTherapyFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.FamilyTherapy["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="WithoutBeneficiary"
                        label="Family Therapy without Beneficiary"
                        checked={
                          data.WithoutBeneficiary &&
                          data.WithoutBeneficiary["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="WithoutBeneficiaryUnits"
                        error={errors.WithoutBeneficiaryUnits}
                        value={
                          data.WithoutBeneficiaryUnits &&
                          data.WithoutBeneficiaryUnits["@value"]
                        }
                        onChange={handleChange}
                        identical={compare("WithoutBeneficiaryUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          !data.WithoutBeneficiary ||
                          data.WithoutBeneficiary["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="WithoutBeneficiaryFrequency"
                        error={errors.WithoutBeneficiaryFrequency}
                        value={
                          data.WithoutBeneficiaryFrequency &&
                          data.WithoutBeneficiaryFrequency["@value"]
                        }
                        onChange={handleChange}
                        identical={compare("WithoutBeneficiaryFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          !data.WithoutBeneficiary ||
                          data.WithoutBeneficiary["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="CrisisManagement"
                        label="Crisis Management (CM)"
                        checked={data.CrisisManagement["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="CrisisManagementUnits"
                        error={errors.CrisisManagementUnits}
                        value={data.CrisisManagementUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("CrisisManagementUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.CrisisManagement["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="CrisisManagementFrequency"
                        error={errors.CrisisManagementFrequency}
                        value={data.CrisisManagementFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("CrisisManagementFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.CrisisManagement["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="ServicePlan"
                        label="Service Plan Development (SPD)"
                        checked={data.ServicePlan["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="ServicePlanUnits"
                        error={errors.ServicePlanUnits}
                        value={data.ServicePlanUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("ServicePlanUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled || data.ServicePlan["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="ServicePlanFrequency"
                        error={errors.ServicePlanFrequency}
                        value={data.ServicePlanFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("ServicePlanFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled || data.ServicePlan["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="h6">Community Support Services:</div>
              <div className="ms-3">
                <div>
                  Details: Interference with at least two or more of the
                  following:
                  <div className="ms-3">
                    <Checkbox
                      name="CommunityDailyLivingTasks"
                      label="Tasks of Daily Living"
                      checked={
                        data.CommunityDailyLivingTasks["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <Checkbox
                      name="CommunityPersonalRelationships"
                      label="Personal Relationships"
                      checked={
                        data.CommunityPersonalRelationships["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <Checkbox
                      name="CommunityWork"
                      label="Work/School"
                      checked={data.CommunityWork["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <Checkbox
                      name="CommunityRecreation"
                      label="Recreation"
                      checked={data.CommunityRecreation["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div>
                  In addition:
                  <br />
                  <ul>
                    <li>
                      Beneficiary is expected to benefit from the intervention
                      and needs would not be better met by any other formal or
                      informal system or support.
                    </li>
                    <li>
                      Family/caregiver agrees to be an active participant in
                      treatment.
                    </li>
                  </ul>
                </div>
                <br />
                Recommendations
                <br />
                <div className="container border">
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">Service</div>
                    <div className="col py-2 border-end"># of Units</div>
                    <div className="col py-2">
                      Frequency (per day/per week/per month)
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="NoCommunitySupport"
                        label="No Community Support Recommended"
                        checked={data.NoCommunitySupport["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end"></div>
                    <div className="col py-2"></div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="Psychosocial"
                        label="Psychosocial Rehabilitative Service (PRS)"
                        checked={data.Psychosocial["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="PsychosocialUnits"
                        error={errors.PsychosocialUnits}
                        value={data.PsychosocialUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("PsychosocialUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled || data.Psychosocial["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="PsychosocialFrequency"
                        error={errors.PsychosocialFrequency}
                        value={data.PsychosocialFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("PsychosocialFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled || data.Psychosocial["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="FamilySupport"
                        label="Family Support (FS)"
                        checked={data.FamilySupport["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="FamilySupportUnits"
                        error={errors.FamilySupportUnits}
                        value={data.FamilySupportUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("FamilySupportUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.FamilySupport["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="FamilySupportFrequency"
                        error={errors.FamilySupportFrequency}
                        value={data.FamilySupportFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("FamilySupportFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.FamilySupport["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="BehaviorModification"
                        label="Behavior Modification (B-Mod)"
                        checked={data.BehaviorModification["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="BehaviorModificationUnits"
                        error={errors.BehaviorModificationUnits}
                        value={data.BehaviorModificationUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("BehaviorModificationUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.BehaviorModification["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="BehaviorModificationFrequency"
                        error={errors.BehaviorModificationFrequency}
                        value={data.BehaviorModificationFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("BehaviorModificationFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.BehaviorModification["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="CommunityCrisisManagement"
                        label="Crisis Management (CM)"
                        checked={
                          data.CommunityCrisisManagement["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="CommunityCrisisManagementUnits"
                        error={errors.CommunityCrisisManagementUnits}
                        value={data.CommunityCrisisManagementUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("CommunityCrisisManagementUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.CommunityCrisisManagement["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="CommunityCrisisManagementFrequency"
                        error={errors.CommunityCrisisManagementFrequency}
                        value={
                          data.CommunityCrisisManagementFrequency["@value"]
                        }
                        onChange={handleChange}
                        identical={compare(
                          "CommunityCrisisManagementFrequency"
                        )}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.CommunityCrisisManagement["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="CommunityServicePlan"
                        label="Service Plan Development (SPD)"
                        checked={data.CommunityServicePlan["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="CommunityServicePlanUnits"
                        error={errors.CommunityServicePlanUnits}
                        value={data.CommunityServicePlanUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("CommunityServicePlanUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.CommunityServicePlan["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="CommunityServicePlanFrequency"
                        error={errors.CommunityServicePlanFrequency}
                        value={data.CommunityServicePlanFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("CommunityServicePlanFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.CommunityServicePlan["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="FosterCare"
                        label="Therapeutic Foster Care (TFC)"
                        checked={data.FosterCare["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">No units required</div>
                    <div className="col py-2">No frequency required</div>
                  </div>
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SCTFCDiagnostic;
